<script setup lang="ts">

import { onMounted, ref } from "vue";
import { useRouter } from '@library/Services';
import { useViewModelInstance } from "@app/MyGarage";
import { useBackButton } from "@app/Components/Utilities";
import { Field } from '@library/Components/Forms/Form';
import type { IVehicleEditableProperties } from '@domain/Vehicle';
import { VehicleUsage } from '@domain/Vehicle/VehicleUsage';
import { useUserSettings } from "@services/UserSettings";
import { States } from "@domain/General";
import { makeForm } from "@library/Components/Forms/Form";

onMounted( () =>
{
    useBackButton( hasRequiredProperties.value );
} );

const model = await useViewModelInstance();
const vehicle = model.vehicle;
const hasRequiredProperties = ref( vehicle.hasRequiredProperties );
const userSettings = useUserSettings();

// The form model that will be edited
const formModel = ref<IVehicleEditableProperties>(
    {
        make: vehicle.make,
        model: vehicle.model,
        rego: vehicle.rego,
        regoState: vehicle.regoState,
        nickname: vehicle.nickname,
        odometer: vehicle.odometer,
        vehicleUsage:  model.vehicle.vehicleUsage == VehicleUsage.Unknown ? undefined : model.vehicle.vehicleUsage
    }
);

const form = makeForm( formModel.value );
const needsVehicleUsage = ref( formModel.value.vehicleUsage == undefined );

// The validation rules
const validation = {
    make: [
        Field.required( `The make field is required`, 2, 50 ),
    ],
    model: [
        Field.required( `The model field is required`, 2, 50 ),
    ],
    nickname: [
        Field.required( `The nickname field is required`, 2 ),
    ],
    rego: [
        Field.required( `The rego field is required`, 2, 9 ),
        Field.regoNumber( 'Must be a valid rego' )
    ],
    regoState: [
        Field.required( `The rego state field is required`, 2 ),
    ],
    odometer: [
        Field.required( `The odometer field is required`, 0, 99999999 ),
        Field.numbersOnly( 'Must be a valid number of kilometers' )
    ],
    vehicleUsage: [
        Field.required( `The estimated annual km's field is required` ),
    ]
};

/**
 * Saves the form
 */
async function saveAsync()
{

    Object.assign( vehicle, formModel.value );

    const updateResult = await model.onVehicleDetailsUpdatedAsync( formModel.value as IVehicleEditableProperties );

    if( updateResult )
    {
        returnToMyGarage();
    }
}

/**
 * Return to the my garage page
 */
function returnToMyGarage()
{
    const router = useRouter();
    router.go( '/my-garage' );
}

</script>

<template>
 
    <div class="bg-gradient-to-b dark:from-gray-900 dark:to-gray-850
                text-base lg:text-lg
                w-full h-full overflow-y-auto overflow-x-hidden flex flex-col pb-12">
                    
        <div>

            <div class="p-6 pb-0 text-center">
                <h2 class="text-xl 3xl:text-2xl tracking-wider flex gap-2 justify-center items-center">
                      Vehicle Details
                </h2>
                <p class="text-sm lg:text-base 3xl:text-xl pt-4 opacity-60">
                    Give your car a nickname for your garage profile and setup your vehicle
                    details
                </p>
            </div>   

        </div>

        <!-- VEHICLE PROPERTIES -->
        <Form class="flex flex-col w-full flex-1 justify-start"
              :form="form"
              v-slot="{form}"
              @ok="() => saveAsync()">

            <div v-if="hasRequiredProperties == false">

                <!-- IF THE ODOMETER IS NULL OR THE USAGE IS NOT SET, INTRODUCE A NEED TO CAPTURE IT -->
                <div class="p-6 flex flex-col gap-2 tracking-wider text-base">
                    <p>
                        Before we go into the garage, we need to confirm a few things about your car
                        and set your odometer.
                    </p>

                    <p>
                        How many kilometers has the car done? 
                    </p>
                    
                </div>

                <Spacer />

            </div>

            <!-- FORM FIELDS -->
            <div class="p-6 flex flex-col h-full justify-between">
            
                <div class="flex flex-col gap-y-4">
                        
                    <TextField v-model="formModel.make" 
                               label="Vehicle Make"
                               required
                               hide-details="auto"
                               :rules="validation.make"
                               :disabled="model.vehicle.make != null && model.vehicle.make.length > 0" />

                    <TextField v-model="formModel.model" 
                               label="Vehicle Model"
                               required
                               hide-details="auto"
                               :rules="validation.model"
                               :disabled="model.vehicle.model != null && model.vehicle.model.length > 0" />
                    
                    <TextField v-model="formModel.nickname" 
                               required
                               :rules="validation.nickname"
                               label="Nickname"
                               hide-details="auto"
                               hint="Give your car a nickname" />

                    <TextField v-model="formModel.rego" 
                               disabled
                               required
                               :rules="validation.rego"
                               label="Rego"
                               hide-details="auto"
                               maxlength="9"
                               hint="Enter your vehicle rego" />

                    <SelectList label="Vehicle Registration State" 
                                placeholder="Select registration state"
                                v-model="formModel.regoState" 
                                :rules="validation.regoState" 
                                required
                                hide-details="auto"
                                :items="States.toListItems()" />

                    <NumberTextField 
                        v-model="formModel.odometer"
                        label="Odometer"
                        hint="Enter your current odometer reading" 
                        hide-details="auto"
                        required
                        maxlength="7"
                        max="2000000"
                        :min="0"
                        :interval="500"
                        class="w-full">
                        <template #append-inner>km</template>
                    </NumberTextField>
                    
                <!-- <div v-if="needsVehicleUsage"
                         class="mt-3 tracking-wider">
                        <h1>How many kilometers do you do in a year?</h1>         
                        <RadioGroup v-model="formModel.vehicleUsage"
                                    :rules="validation.vehicleUsage">
                            <RadioButton label="Low < 10,000km per year" :value="VehicleUsage.Low"></RadioButton>
                            <RadioButton label="Medium < 15,000km per year" :value="VehicleUsage.Medium"></RadioButton>
                            <RadioButton label="High > 20,000km per year" :value="VehicleUsage.High"></RadioButton>
                        </RadioGroup>
                    </div> -->
                    
                    <Button variant="tonal"
                            :disabled="form.isValid == false"
                            type="submit"
                            size="large"
                            class="mt-4 mx-auto !px-8">
                        Update Details
                    </Button>

                </div>
                
            </div>

        </Form>
                
    </div>

</template>
